/* Copyright (C) Okahu Inc 2023-2024. All rights reserved.*/

import * as go from 'gojs';

import {
  repositionGroups,
  setDescendantsVisibility,
} from '../../../helpers/graph';
import {
  EXPANDER_STYLES,
  NODE_COMMON_STYLES,
  NODE_STYLES,
  TOOLTIP_STYLES,
} from '../graph-config';
import { GoMake } from './shared';

/**
 * Turn node template with expander button
 */
export function createTurnNodeTemplate($: GoMake): go.Node {
  return $(
    go.Node,
    'Spot',
    {
      selectionAdorned: false,
      locationSpot: go.Spot.Center,
      locationObjectName: 'MAIN_PANEL',
      selectionChanged: (node: go.Part) => {
        const n = node as go.Node;
        const outerRing = n.findObject('OUTER_RING') as go.Shape;
        if (n.isSelected) {
          // Always show outer ring on selection, even if collapsed
          if (outerRing) {
            outerRing.visible = true;
            outerRing.stroke = NODE_STYLES.turn.selected.stroke;
          }
          n.isShadowed = true;
          // Jade glow around the outer ring — matches box-shadow: 0px 0px 6px 1px var(--Jade)
          n.shadowColor = NODE_STYLES.turn.selected.shadowColor;
          n.shadowBlur = NODE_STYLES.turn.selected.shadowBlur;
          n.shadowOffset = new go.Point(
            NODE_STYLES.turn.selected.shadowOffset.x,
            NODE_STYLES.turn.selected.shadowOffset.y
          );
        } else {
          if (outerRing) {
            // Hide outer ring on deselect if node is collapsed — binding alone is
            // not enough here since we forced it visible on selection
            const isError = n.data?.status?.code === 'ERROR';
            outerRing.visible = !!n.data.isExpanded || isError;
            outerRing.stroke = isError
              ? NODE_COMMON_STYLES.error.stroke
              : NODE_STYLES.turn.outerRing.stroke;
          }
          // Do NOT reset circle.stroke or circle.strokeWidth here —
          // the isExpanded bindings on TURN_CIRCLE handle those correctly.
          // Imperatively setting them here would override the bindings and
          // cause the wrong stroke/strokeWidth when deselecting a collapsed node.
          n.isShadowed = false;
        }
      },
      mouseEnter: (_e: go.InputEvent, obj: go.GraphObject) => {
        const node = obj as go.Node;
        if (node.isSelected) return;
        const outerRing = node.findObject('OUTER_RING') as go.Shape;
        if (outerRing) {
          // TEMP: Outer ring made visible on hover even when collapsed.
          // Remove the line below (and the matching one in mouseLeave) if
          // hover should only affect already-expanded nodes.
          outerRing.visible = true;
          outerRing.stroke = NODE_STYLES.turn.hover.stroke;
        }
      },
      mouseLeave: (_e: go.InputEvent, obj: go.GraphObject) => {
        const node = obj as go.Node;
        if (node.isSelected) return;
        const outerRing = node.findObject('OUTER_RING') as go.Shape;
        if (outerRing) {
          // TEMP: Hide outer ring on mouse-leave if node is collapsed.
          // Remove the line below (and the matching one in mouseEnter) if
          // hover should only affect already-expanded nodes.
          const isError = node.data?.status?.code === 'ERROR';
          outerRing.visible = !!node.data.isExpanded || isError;
          outerRing.stroke = isError
            ? NODE_COMMON_STYLES.error.stroke
            : NODE_STYLES.turn.outerRing.stroke;
        }
        node.isShadowed = false;
      },
      // Tooltip for showing full label on hover; error variant mirrors link tooltip
      toolTip: $(
        go.Adornment,
        'Auto',
        new go.Binding(
          'visible',
          '',
          (data) => !!data.tooltip || data?.status?.code === 'ERROR'
        ),
        $(
          go.Shape,
          'RoundedRectangle',
          new go.Binding('fill', 'status', (s) =>
            s?.code === 'ERROR'
              ? TOOLTIP_STYLES.error.fill
              : TOOLTIP_STYLES.background.fill
          ),
          new go.Binding('stroke', 'status', (s) =>
            s?.code === 'ERROR'
              ? TOOLTIP_STYLES.error.stroke
              : TOOLTIP_STYLES.background.stroke
          )
        ),
        $(
          go.Panel,
          'Vertical',
          {
            defaultAlignment: go.Spot.Left,
            minSize: new go.Size(TOOLTIP_STYLES.minWidth, 0),
          },
          new go.Binding('margin', 'status', (s) =>
            s?.code === 'ERROR'
              ? TOOLTIP_STYLES.link.errorPanelMargin
              : TOOLTIP_STYLES.link.panelMargin
          ),
          // Error header row: badge icon + "Error" label — only shown for ERROR status
          $(
            go.Panel,
            'Horizontal',
            {
              visible: false,
              defaultAlignment: go.Spot.Center,
              margin: new go.Margin(0, 0, 8, 0),
            },
            new go.Binding('visible', 'status', (s) => s?.code === 'ERROR'),
            $(go.Picture, {
              source: '/logos/error-badge-outline.svg',
              width: 16,
              height: 16,
              margin: new go.Margin(0, 4, 0, 0),
            }),
            $(go.TextBlock, {
              text: 'Error',
              font: TOOLTIP_STYLES.error.font,
              stroke: TOOLTIP_STYLES.link.title.stroke,
            })
          ),
          // Separator line — only shown for ERROR status
          $(
            go.Shape,
            'Rectangle',
            {
              height: 1,
              fill: TOOLTIP_STYLES.error.dividerStroke,
              strokeWidth: 0,
              stretch: go.Stretch.Horizontal,
              visible: false,
              margin: new go.Margin(0, 0, 12, 0),
            },
            new go.Binding('visible', 'status', (s) => s?.code === 'ERROR')
          ),
          // Turn ID / label
          $(
            go.TextBlock,
            {
              visible: false,
              margin: TOOLTIP_STYLES.text.margin,
              font: TOOLTIP_STYLES.text.font,
              stroke: TOOLTIP_STYLES.text.stroke,
              wrap: go.Wrap.Fit,
              maxSize: TOOLTIP_STYLES.text.maxSize,
            },
            new go.Binding('visible', 'tooltip', (t) => !!t),
            new go.Binding('text', 'tooltip')
          ),
          // Duration
          $(
            go.TextBlock,
            {
              visible: false,
              margin: TOOLTIP_STYLES.text.margin,
              font: TOOLTIP_STYLES.text.font,
              stroke: TOOLTIP_STYLES.text.stroke,
              wrap: go.Wrap.Fit,
              maxSize: TOOLTIP_STYLES.text.maxSize,
            },
            new go.Binding('visible', 'duration', (d: string) => !!d),
            new go.Binding('text', 'duration', (d: string) =>
              d ? `Duration: ${d}` : null
            )
          ),
          // Status message / error description
          $(
            go.TextBlock,
            {
              visible: false,
              margin: TOOLTIP_STYLES.text.margin,
              font: TOOLTIP_STYLES.text.font,
              stroke: TOOLTIP_STYLES.text.stroke,
              wrap: go.Wrap.Fit,
              maxSize: TOOLTIP_STYLES.text.maxSize,
            },
            new go.Binding(
              'visible',
              'status',
              (s) => s?.code === 'ERROR' && !!s?.message
            ),
            new go.Binding('text', 'status', (s) => s?.message ?? null)
          )
        )
      ),
    },
    new go.Binding('location', 'location', go.Point.parse, go.Point.stringify),
    // Main content: Vertical panel with expander, labels below
    $(
      go.Panel,
      'Vertical',
      {
        alignment: go.Spot.Center,
        name: 'MAIN_PANEL',
      },
      // Circle with icon inside (Auto panel centers content automatically)
      $(
        go.Panel,
        'Spot',
        // Outer ring — larger transparent circle for double-ring design
        // Visible only when the turn node is expanded
        $(
          go.Shape,
          'Circle',
          {
            name: 'OUTER_RING',
            desiredSize: NODE_STYLES.turn.outerRing.size,
            fill: NODE_STYLES.turn.outerRing.fill,
            stroke: NODE_STYLES.turn.outerRing.stroke,
            strokeWidth: NODE_STYLES.turn.outerRing.strokeWidth,
          },
          new go.Binding(
            'visible',
            '',
            (data) => !!data.isExpanded || data?.status?.code === 'ERROR'
          ),
          new go.Binding('stroke', 'status', (s) =>
            s?.code === 'ERROR'
              ? NODE_COMMON_STYLES.error.stroke
              : NODE_STYLES.turn.outerRing.stroke
          )
        ),
        // Inner filled circle — strokeWidth 6 when expanded, 3 when collapsed
        $(
          go.Shape,
          NODE_STYLES.turn.shape,
          {
            name: 'TURN_CIRCLE',
            desiredSize: NODE_STYLES.turn.size,
            fill: NODE_STYLES.turn.fill,
            stroke: NODE_STYLES.turn.stroke,
            strokeWidth: NODE_STYLES.turn.strokeWidth,
          },
          new go.Binding('desiredSize', 'isExpanded', (expanded) =>
            expanded ? NODE_STYLES.turn.size : NODE_STYLES.turn.size2
          ),
          new go.Binding('strokeWidth', 'isExpanded', (expanded) =>
            expanded ? NODE_STYLES.turn.strokeWidth : 3
          ),
          new go.Binding('stroke', 'isExpanded', (expanded) =>
            expanded ? NODE_STYLES.turn.stroke : '#8C9EA1'
          )
        ),
        // Top port (transparent)
        $(go.Shape, 'Circle', {
          width: 2,
          height: 2,
          fill: 'transparent',
          stroke: 'transparent',
          portId: 'top',
          alignment: go.Spot.Top,
          fromLinkable: true,
          toLinkable: true,
        }),
        // Bottom port (transparent)
        $(go.Shape, 'Circle', {
          width: 2,
          height: 2,
          fill: 'transparent',
          stroke: 'transparent',
          portId: 'bottom',
          alignment: go.Spot.Bottom,
          fromLinkable: true,
          toLinkable: true,
        }),
        // Expander button centered on the right edge of the circle
        $(
          go.Panel,
          'Spot',
          {
            alignment: new go.Spot(1, 0.5, 20, 0),
            alignmentFocus: go.Spot.Center,
            cursor: 'pointer',
            click: (e: go.InputEvent, obj: go.GraphObject) => {
              e.handled = true; // Prevent event from propagating
              const node = obj.part as go.Node;
              if (!node) return;
              const diagram = node.diagram;
              if (!diagram) return;

              // Clear selection so the detail panel does not open when the expander is clicked
              diagram.clearSelection();

              const isExpanded = node.data.isExpanded;
              const groupKey = `group_${node.data.key}`;
              const hasChildren = diagram.findNodeForKey(groupKey) !== null;

              // If expanding and children haven't been fetched, fire a custom event
              // so the React component can trigger the API call
              if (!isExpanded && !hasChildren) {
                diagram.startTransaction('request expand');
                // Accordion behavior: collapse all other expanded turn nodes
                diagram.nodes.each((n) => {
                  if (
                    n !== node &&
                    n.data.category === 'turn' &&
                    n.data.isExpanded
                  ) {
                    diagram.model.setDataProperty(n.data, 'isExpanded', false);
                    setDescendantsVisibility(n, false);
                  }
                });
                diagram.model.setDataProperty(node.data, 'isExpanded', true);
                repositionGroups(diagram);
                diagram.commitTransaction('request expand');

                // Raise a custom event for the React component to handle
                diagram.model.raiseChangedEvent(
                  go.ChangeType.Transaction,
                  'TurnExpandRequested',
                  node,
                  null,
                  { turnNodeId: node.data.key, traceId: node.data.traceId }
                );
                return;
              }

              diagram.startTransaction('toggle expand');

              // Accordion behavior: collapse all other expanded turn nodes
              if (!isExpanded) {
                diagram.nodes.each((n) => {
                  if (
                    n !== node &&
                    n.data.category === 'turn' &&
                    n.data.isExpanded
                  ) {
                    diagram.model.setDataProperty(n.data, 'isExpanded', false);
                    setDescendantsVisibility(n, false);
                  }
                });
              }

              // Toggle current node
              diagram.model.setDataProperty(
                node.data,
                'isExpanded',
                !isExpanded
              );
              setDescendantsVisibility(node, !isExpanded);

              // Reposition groups so the expanded group aligns with its turn node
              repositionGroups(diagram);
              diagram.commitTransaction('toggle expand');
            },
          },
          $(go.Shape, 'Circle', {
            width: EXPANDER_STYLES.size,
            height: EXPANDER_STYLES.size,
            fill: EXPANDER_STYLES.fill,
            stroke: EXPANDER_STYLES.stroke,
            strokeWidth: EXPANDER_STYLES.strokeWidth,
          }),
          // Chevron icon (> collapsed, < expanded)
          $(
            go.TextBlock,
            {
              alignment: go.Spot.Center,
              font: 'bold 12px sans-serif',
              stroke: EXPANDER_STYLES.iconStroke,
            },
            new go.Binding('text', 'isExpanded', (expanded) =>
              expanded ? '\u2039' : '\u203A'
            )
          ),
          // Right port on the expander button - links to agents/tools start here
          $(go.Shape, 'Circle', {
            width: 2,
            height: 2,
            fill: 'transparent',
            stroke: 'transparent',
            portId: 'right',
            alignment: go.Spot.Right,
            fromLinkable: true,
            toLinkable: true,
          })
        ),
        // Horizontal line connecting node circle edge to expander button
        $(go.Shape, 'LineH', {
          alignment: new go.Spot(1, 0.5, 0, 0),
          alignmentFocus: go.Spot.Left,
          width: 10,
          height: 1,
          stroke: NODE_STYLES.turn.outerRing.stroke,
          strokeWidth: 1,
          fill: null,
        })
      ),
      // Label below the circle
      $(
        go.TextBlock,
        {
          font: NODE_COMMON_STYLES.labelFont,
          stroke: NODE_STYLES.turn.textStroke,
          textAlign: 'center',
          margin: NODE_COMMON_STYLES.labelMargin,
        },
        new go.Binding('text', 'duration')
      )
    )
  );
}

/**
 * Start the pagination loading pulse on the synthetic 'turn_loader' placeholder
 * node. Outer ring scales up + fades; inner dot pulses opacity with a half-cycle
 * phase offset for a ripple feel. Animations target Shape properties directly so
 * the GoJS model is not mutated per frame.
 */
export function startTurnPaginationAnimation(diagram: go.Diagram): void {
  stopTurnPaginationAnimation(diagram);

  const maxScale = NODE_STYLES.turn.pulseRing.maxScale;

  const ringAnim = new go.Animation();
  ringAnim.easing = go.Animation.EaseInOutQuad;
  ringAnim.duration = 1500;
  ringAnim.reversible = false;
  ringAnim.runCount = Infinity;

  const dotAnim = new go.Animation();
  dotAnim.easing = go.Animation.EaseInOutQuad;
  dotAnim.duration = 1500;
  dotAnim.reversible = true;
  dotAnim.runCount = Infinity;

  let hasRing = false;
  let hasDot = false;
  diagram.nodes.each((node) => {
    if (node instanceof go.Group) return;
    if (node.data?.category !== 'turn_loader') return;
    const ring = node.findObject('PULSE_RING') as go.Shape | null;
    if (ring) {
      ring.scale = 1;
      ring.opacity = 0.9;
      ringAnim.add(ring, 'scale', 1.0, maxScale);
      ringAnim.add(ring, 'opacity', 0.9, 0.0);
      hasRing = true;
    }
    const dot = node.findObject('PULSE_DOT') as go.Shape | null;
    if (dot) {
      dot.opacity = 1.0;
      dotAnim.add(dot, 'opacity', 1.0, 0.6);
      hasDot = true;
    }
  });

  const d = diagram as unknown as Record<string, unknown>;
  if (hasRing) {
    d.__turnPulseRingAnimation = ringAnim;
    ringAnim.start();
  }
  if (hasDot) {
    d.__turnPulseDotAnimation = dotAnim;
    const handle = setTimeout(() => {
      if (d.__turnPulseDotAnimation === dotAnim) dotAnim.start();
    }, 750);
    d.__turnPulseDotTimeout = handle;
  }
}

export function stopTurnPaginationAnimation(diagram: go.Diagram): void {
  const d = diagram as unknown as Record<string, unknown>;

  const ring = d.__turnPulseRingAnimation as go.Animation | undefined;
  if (ring) {
    ring.stop();
    delete d.__turnPulseRingAnimation;
  }
  const dot = d.__turnPulseDotAnimation as go.Animation | undefined;
  if (dot) {
    dot.stop();
    delete d.__turnPulseDotAnimation;
  }
  const pendingDot = d.__turnPulseDotTimeout as
    | ReturnType<typeof setTimeout>
    | undefined;
  if (pendingDot !== undefined) {
    clearTimeout(pendingDot);
    delete d.__turnPulseDotTimeout;
  }

  diagram.nodes.each((node) => {
    if (node instanceof go.Group) return;
    if (node.data?.category !== 'turn_loader') return;
    const r = node.findObject('PULSE_RING') as go.Shape | null;
    if (r) {
      r.scale = 1;
      r.opacity = 0.9;
    }
    const dt = node.findObject('PULSE_DOT') as go.Shape | null;
    if (dt) {
      dt.opacity = 1;
    }
  });
}
